import { ACTION_TYPES } from '../../constants/protocol';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  handleApplyCommand,
  handleCommandInfo,
  handleControllersStatus,
  handleSaveCommand,
  selectMCCommand,
} from '../store/slices/mCCommandSlice';
import {
  selectMessageBoxes,
  handleApplyMessageBox,
} from '../store/slices/messageBoxesSlice';
import { selectedMachinesState } from '../store/slices/selectedMachinesSlice';
import { selectMC } from '../store/slices/mCSlice';
import {
  handleActivateHeatingSchedule,
  handleAddHeatingSchedule,
  handleAtsSelection,
  handleClearHeatingSchedule,
  handleHeatingScheduleReset,
  handleInstantHeat,
  handleInstantHeatOff,
  handleInstantHeatReady,
  handleInstantHeatReset,
  handleOptionalConstantTemp,
  handleOptionalConstantTempOff,
  handleOptionalConstantTempReady,
  handleOptionalConstantTempReadyOff,
  handleOptionalConstantTempReset,
  handleReadyHeatingSchedule,
  handleSnowSensor,
  handleSnowSensorIsActivatedOff,
  handleSnowSensorOff,
  handleSnowSensorReset,
  handleWindFactor,
  handleWindFactorIsActivatedOff,
  handleWindFactorOff,
  handleWindFactorReset,
  selectEssSwitch,
} from '../store/slices/essSwitchSlice';
import {
  selectTesSwitch,
  tesHandleAddHeatingSchedule,
  tesHandleAtsSelection,
  tesHandleHeatingScheduleReset,
  tesHandleInstantHeat,
  tesHandleInstantHeatIsReady,
  tesHandleInstantHeatReset,
  tesHandleOptionalConstantTemp,
  tesHandleOptionalConstantTempIsReady,
  tesHandleOptionalConstantTempReset,
  tesHandleReadyHeatingSchedule,
  tesHandleSnowSensor,
  tesHandleSnowSensorReset,
  tesHandleWindFactor,
  tesHandleWindFactorReset,
} from '../store/slices/tesSwitchSlice';
import {
  selectTgsSwitch,
  tgsHandleInstantHeat,
  tgsHandleSnowSensor,
  tgsHandleAddHeatingSchedule,
  tgsHandleWindFactor,
  tgsHandleFanOnly,
  tgsHandleAtsSelection,
  tgsHandleInstantHeatReset,
  tgsHandleFanOnlyReset,
  tgsHandleSnowSensorReset,
  tgsHandleHeatingScheduleReset,
  tgsHandleWindFactorReset,
  tgsHandleReadyHeatingSchedule,
  tgsHandleInstantHeatIsReady,
} from '../store/slices/tgsSwitchSlice';

import styled from 'styled-components';
import {
  flexBoxCenter,
  flexDirectionColumn,
  justifyContentSpaceBetween,
  layerA180Deg,
  layerADark,
} from '../styles/commonStyles';
import DisplayDialAndSections from './controls/mainSelection/DisplayDialAndSections';
import ViewCommand from './userMessages/ViewCommand';
import SelectSystemApplyMessageBox from './userMessages/SelectSystemApplyMessageBox';
import InputTempMessage from '../userMessages/inputTempMessage';
import MasterControlCommandMain from './M.C.Command/MasterControlCommandMain';
import { selectUserInfo } from '../store/slices/userSlice';
import moment from 'moment';
import { selectUnits } from '../store/slices/settings/unitsSlice';
import { masterControl } from '../../services/masterControl.service';
import {
  getSelectedSnowThreshold,
  savedCommandTemp,
  toDisplayTemp,
} from '../../helpers/helpers';
import { getAuditTrailService } from '../../services';
import { clear } from '@testing-library/user-event/dist/clear';
import testData from '../../test_data/testData';
import { selectLocations } from '../store/slices/locationsSlice';
import { selectMasterControls } from '../store/slices/masterControlSelectSlice';
import { logger } from '../../helpers/logger';
import { reportMasterControlFailure } from '../store/slices/commandFailureSlice';
import { globalMasterControlPayload } from '../../helpers/payloads/globalMasterControlPayload';

const MasterControlContents = ({
  setCommandConfirmed,
  handleCreateNewCommand,
  handleCreateNewCommandMessageBox,
  messageBoxOfCreateNewCommand,
  handleMessageBoxOfSelectSystem,
}) => {
  const dispatch = useDispatch();
  const mCCommandState = useSelector(selectMCCommand);
  const number = mCCommandState.commandNumber;
  const messageBoxesState = useSelector(selectMessageBoxes);
  const selectedMachines = useSelector(selectedMachinesState);
  const { flatEssSwitch } = useSelector(selectEssSwitch);
  const { flatTesSwitch } = useSelector(selectTesSwitch);
  const { flatTgsSwitch } = useSelector(selectTgsSwitch);
  const selectedSwitch = useSelector(selectMC);
  const UserState = useSelector(selectUserInfo);
  const { user } = UserState;
  const { viewCommand } = mCCommandState;
  const { applyButton } = messageBoxesState;
  const instantHeatState = selectedMachines.instantHeat;
  const { ready, defaultTemp } = selectedMachines.snowSensor;
  const constantTempState = selectedMachines.optionalConstantTemp;
  const heatingScheduleState = selectedMachines.heatingSchedule.ready;
  const heatingScheduleTemp = selectedMachines.heatingScheduleList[0].inputTemp;
  const heatingScheduleStart = selectedMachines.heatingScheduleList[0].start;
  const heatingScheduleEnd = selectedMachines.heatingScheduleList[0].end;
  const scheduleDates = selectedMachines.heatingScheduleList[0];
  const isWindFactorState = selectedMachines.windFactor.ready;
  const isFanOnlyState = selectedMachines.isFanOnly;
  const { ess, tgs, tes, hp } = selectedSwitch.selectSystem;
  const {
    isFanOnly,
    heatingSchedule,
    instantHeat,
    optionalConstantTemp,
    snowSensor,
    windFactor,
  } = selectedMachines.mCOff;

  const { atsState } = selectedMachines;
  const { swt, selections } = atsState;
  const unitState = useSelector(selectUnits);
  const { isF } = unitState;
  const snowThreshold = getSelectedSnowThreshold(
    tgs ? flatTgsSwitch : tes ? flatTesSwitch : flatEssSwitch,
    isF,
  );

  const hasProgramChanges =
    instantHeatState.ready ||
    ready ||
    constantTempState.ready ||
    heatingScheduleState ||
    isWindFactorState ||
    isFanOnlyState ||
    instantHeat ||
    snowSensor ||
    optionalConstantTemp ||
    heatingSchedule ||
    windFactor ||
    isFanOnly ||
    selections?.some((selection) => selection === true);

  const [messageBoxContent, setMessageBoxContent] = useState({});
  const [componentTitle, setComponentTitle] = useState(null);
  const [displayNoChangesMessage, setDisplayNoChangesMessage] = useState(false);
  const [selectedSwitches, setSelectedSwitches] = useState([]);

  const date = moment().format('DDMMYY');

  useEffect(() => {
    if (ess) {
      setComponentTitle('ess');
    } else if (tes) {
      setComponentTitle('tes');
    } else if (tgs) {
      setComponentTitle('tgs');
    } else if (hp) {
      setComponentTitle('hp');
    } else {
      setComponentTitle(null);
    }
  }, [selectedSwitch]);
  let apiCallMade = false;

  const heatingProgramHandler = (
    location,
    machine,
    specificLocation,
    system,
    deviceIds,
    newData,
  ) => {
    if (instantHeat) {
      switch (system) {
        case 'ess':
          dispatch(
            handleInstantHeatReset({
              location,
              machine,
              specificLocation,
            }),
          );
          break;
        case 'tgs':
          dispatch(
            tgsHandleInstantHeatReset({ location, machine, specificLocation }),
          );
          break;
        case 'tes':
          dispatch(
            tesHandleInstantHeatReset({ location, machine, specificLocation }),
          );
          break;
        default:
          break;
      }
    } else if (instantHeatState.ready) {
      dispatch(handleControllersStatus(0));

      switch (system) {
        case 'ess':
          dispatch(
            handleInstantHeatReady({
              location,
              specificLocation,
              machine,
              temp: instantHeatState.temp,
            }),
          );

          break;
        case 'tgs':
          dispatch(
            tgsHandleInstantHeatIsReady({
              location,
              specificLocation,
              machine,
              temp: instantHeatState.temp,
            }),
          );

          break;
        case 'tes':
          dispatch(
            tesHandleInstantHeatIsReady({
              location,
              specificLocation,
              machine,
              temp: instantHeatState.temp,
            }),
          );
          break;
        default:
          break;
      }
    } else {
      logger.debug('instantHeatXXXX');
    }
    if (snowSensor) {

      switch (system) {
        case 'ess':
          dispatch(
            handleSnowSensorReset({
              location,
              specificLocation,
              machine,
            }),
          );
          break;
        case 'tgs':
          dispatch(
            tgsHandleSnowSensorReset({ location, specificLocation, machine }),
          );
          break;
        case 'tes':
          dispatch(
            tesHandleSnowSensorReset({ location, specificLocation, machine }),
          );
          break;
        default:
          break;
      }
    } else if (ready) {
      dispatch(handleControllersStatus(1));

      switch (system) {
        case 'ess':
          dispatch(handleSnowSensor({ location, specificLocation, machine }));
          break;
        case 'tgs':
          dispatch(
            tgsHandleSnowSensor({ location, specificLocation, machine }),
          );
          break;
        case 'tes':
          dispatch(
            tesHandleSnowSensor({ location, specificLocation, machine }),
          );
          break;
        default:
          break;
      }
    }
    if (system === 'tgs') {
      if (isFanOnly) {
        dispatch(
          tgsHandleFanOnly({
            location,
            machine,
            specificLocation,
            state: false,
          }),
        );
      } else if (isFanOnlyState) {
        dispatch(handleControllersStatus(2));
        dispatch(
          tgsHandleFanOnly({
            location,
            machine,
            specificLocation,
            state: true,
          }),
        );
      }
    }
    if (system !== 'tgs') {
      if (optionalConstantTemp) {
        if (system === 'ess') {
          dispatch(
            handleOptionalConstantTempReset({
              location,
              specificLocation,
              machine,
            }),
          );
        } else if (system === 'tes') {
          dispatch(
            tesHandleOptionalConstantTempReset({
              location,
              specificLocation,
              machine,
            }),
          );
        }
      } else if (constantTempState.ready) {
        dispatch(handleControllersStatus(2));
        if (system === 'ess') {
          dispatch(
            handleOptionalConstantTempReady({
              location,
              specificLocation,
              machine,
              temp: constantTempState.temp,
            }),
          );
        } else if (system === 'tes') {
          dispatch(
            tesHandleOptionalConstantTempIsReady({
              location,
              specificLocation,
              machine,
              temp: constantTempState.temp,
            }),
          );
        }
      }
    }
    if (heatingSchedule) {
      switch (system) {
        case 'ess':
          dispatch(
            handleHeatingScheduleReset({
              location,
              specificLocation,
              machine,
            }),
          );

          break;
        case 'tgs':
          dispatch(
            tgsHandleHeatingScheduleReset({
              location,
              specificLocation,
              machine,
            }),
          );

          break;
        case 'tes':
          dispatch(
            tesHandleHeatingScheduleReset({
              location,
              specificLocation,
              machine,
            }),
          );
          break;
        default:
          break;
      }
    } else if (heatingScheduleState) {
      dispatch(handleControllersStatus(3));

      switch (system) {
        case 'ess':
          dispatch(
            handleReadyHeatingSchedule({
              location,
              machine,
              specificLocation,
              state: true,
            }),
          );
          dispatch(
            handleAddHeatingSchedule({
              location,
              specificLocation,
              machine,
              start: heatingScheduleStart,
              end: heatingScheduleEnd,
              inputTemp: heatingScheduleTemp,
              index: 0,
            }),
          );

          break;
        case 'tgs':
          dispatch(
            tgsHandleReadyHeatingSchedule({
              location,
              machine,
              specificLocation,
              state: true,
            }),
          );
          dispatch(
            tgsHandleAddHeatingSchedule({
              location,
              specificLocation,
              machine,
              start: heatingScheduleStart,
              end: heatingScheduleEnd,
              inputTemp: heatingScheduleTemp,
              index: 0,
            }),
          );

          break;
        case 'tes':
          dispatch(
            tesHandleReadyHeatingSchedule({
              location,
              specificLocation,
              machine,
              state: true,
            }),
          );
          dispatch(
            tesHandleAddHeatingSchedule({
              location,
              specificLocation,
              machine,
              start: heatingScheduleStart,
              end: heatingScheduleEnd,
              inputTemp: heatingScheduleTemp,
              index: 0,
            }),
          );
          break;
        default:
          break;
      }
    }
    if (windFactor) {

      switch (system) {
        case 'ess':
          dispatch(
            handleWindFactorReset({ location, specificLocation, machine }),
          );
          break;
        case 'tgs':
          dispatch(
            tgsHandleWindFactorReset({ location, specificLocation, machine }),
          );
          break;
        case 'tes':
          dispatch(
            tesHandleWindFactorReset({ location, specificLocation, machine }),
          );
          break;
        default:
          break;
      }
    } else if (isWindFactorState) {
      dispatch(handleControllersStatus(4));

      switch (system) {
        case 'ess':
          dispatch(handleWindFactor({ location, specificLocation, machine }));
          break;
        case 'tgs':
          dispatch(
            tgsHandleWindFactor({ location, specificLocation, machine }),
          );
          break;
        case 'tes':
          dispatch(
            tesHandleWindFactor({ location, specificLocation, machine }),
          );
          break;
        default:
          break;
      }
    }
    if (swt) {
      switch (system) {
        case 'ess':
          dispatch(
            handleAtsSelection({
              location,
              machine,
              specificLocation,
              selection: selections,
            }),
          );

          break;
        case 'tgs':
          dispatch(
            tgsHandleAtsSelection({
              location,
              machine,
              specificLocation,
              selection: selections,
            }),
          );

          break;
        case 'tes':
          dispatch(
            tesHandleAtsSelection({
              location,
              machine,
              specificLocation,
              selection: selections,
            }),
          );
          break;
        default:
          break;
      }
    }

    if (!apiCallMade) {
      apiCallMade = true;
      const reportFailure = reportMasterControlFailure(
        dispatch,
        system,
        deviceIds,
      );
      masterControl(system, deviceIds, newData)
        .then(() => {
          getAuditTrailService({
            actionType: ACTION_TYPES.GLOBAL_MASTER_CONTROL,
          }).then((res) => {
            dispatch(
              handleCommandInfo({
                data: res,
                user,
                isF,
                flatEssSwitch,
                flatTgsSwitch,
                flatTesSwitch,
              }),
            );
          });
        })
        .catch(reportFailure);
    }

  };

  const saveCommandHandler = (
    userId,
    commandKey,
    locations,
    specificLocations,
    machines,
    sumOfSwitches,
    system,
    option,
    option2,
  ) => {
    const keyValue = Object.entries(option)[0];
    const keyValueAts = Object.entries(option2)[0];
    dispatch(
      handleSaveCommand({
        user: userId,
        commandData: {
          [`${commandKey}`]: {
            locations,
            specificLocations,
            machines,
            system,
            switches: sumOfSwitches,
            [keyValueAts[0]]: keyValueAts[1],
            deactivate: {
              instantHeat,
              isFanOnly,
              heatingSchedule,
              optionalConstantTemp,
              snowSensor,
              windFactor,
            },
            isF,
            parameters: {
              instantHeat: instantHeatState.ready
                ? `active ${savedCommandTemp(instantHeatState.temp, isF)}`
                : null,
              [keyValue[0]]: keyValue[1],
              snowSensor: ready ? 'ready' : null,
              heatingSchedule: heatingScheduleState
                ? `ready ${savedCommandTemp(heatingScheduleTemp, isF)}`
                : null,
              windFactor: isWindFactorState ? 'ready' : null,
            },
          },
        },
      }),
    );
  };

  const loopAllMachinesToDispatchProgramsHandler = (
    switches,
    system,
    deviceIds,
    newData,
  ) => {
    Object.keys(switches).forEach((location) =>
      Object.keys(switches[location]).forEach((el) => {
        if (switches[location][el]?.machineType) {
          if (switches[location][el]?.isSelected) {
            heatingProgramHandler(
              location,
              el,
              null,
              system,
              deviceIds,
              newData,
            );
          }
        } else {
          Object.keys(switches[location][el]).forEach((machine) => {
            if (switches[location][el][machine]?.isSelected) {
              heatingProgramHandler(
                location,
                machine,
                el,
                system,
                deviceIds,
                newData,
              );
            }
          });
        }
      }),
    );
  };

  const handleCreateCommand = useCallback(() => {
    let sumOfSwitches = 0;
    const machines = [];
    const specificLocations = [];
    const deviceIds = [];
    const locations = selectedSwitches.map((value) => {
      const newLocations = Object.keys(value)[0];
      const locationValue = Object.values(value)[0];
      const machineValue = Object.values(locationValue);
      let newMachines;

      if (machineValue.some((el) => el?.deviceMac)) {
        specificLocations.push([]);
        newMachines = Object.keys(locationValue).map((el) => el);
      } else {
        const newSpecificLocations = Object.keys(locationValue);
        specificLocations.push(newSpecificLocations);
        newMachines = Object.keys(Object.values(locationValue)[0]).map(
          (el) => el,
        );
      }

      sumOfSwitches += newMachines.length;
      machines.push(newMachines);
      deviceIds.push(...newMachines);
      return newLocations;
    });

    const newData = globalMasterControlPayload(selectedMachines, number);

    const commandKey = `mca-${user.user_id}-${date}-${
      number < 10 ? `${'0' + number} ` : number
    }`;

    const userId = user.user_id;

    if (tgs) {
      const fanOnly = isFanOnlyState ? 'ready' : null;
      const option = {
        fanOnly,
      };
      const ats = selections[0]
        ? { reactivate: true, block: false }
        : selections[1]
          ? { reactivate: false, block: true }
          : { reactivate: false, block: false };
      const option2 = { ats };

      saveCommandHandler(
        userId,
        commandKey,
        locations,
        specificLocations,
        machines,
        sumOfSwitches,
        'tgs-typhoon gas system',
        option,
        option2,
      );

      loopAllMachinesToDispatchProgramsHandler(
        flatTgsSwitch,
        'tgs',
        deviceIds,
        newData,
      );

    } else if (tes) {
      const optionalConstantTemp = constantTempState.ready
        ? `ready ${savedCommandTemp(constantTempState.temp, isF)}`
        : null;
      const option = {
        optionalConstantTemp,
      };

      const ats = selections[2]
        ? { activateTgs: true, reactivate: false, block: false }
        : selections[0]
          ? { activateTgs: false, reactivate: true, block: false }
          : selections[1]
            ? { activateTgs: false, reactivate: false, block: true }
            : { activateTgs: false, reactivate: false, block: false };

      const option2 = {
        ats,
      };
      saveCommandHandler(
        userId,
        commandKey,
        locations,
        specificLocations,
        machines,
        sumOfSwitches,
        'tes-typhoon electric system',
        option,
        option2,
      );

      loopAllMachinesToDispatchProgramsHandler(
        flatTesSwitch,
        'tes',
        deviceIds,
        newData,
      );
    } else if (ess) {
      const optionalConstantTemp = constantTempState.ready
        ? `ready ${savedCommandTemp(constantTempState.temp, isF)}`
        : null;
      const option = {
        optionalConstantTemp,
      };

      const ats = selections[0]
        ? { reactivate: true, block: false }
        : selections[1]
          ? { reactivate: false, block: true }
          : { reactivate: false, block: false };

      const option2 = {
        ats,
      };

      saveCommandHandler(
        userId,
        commandKey,
        locations,
        specificLocations,
        machines,
        sumOfSwitches,
        'ess-typhoon electric system',
        option,
        option2,
      );

      loopAllMachinesToDispatchProgramsHandler(
        flatEssSwitch,
        'ess',
        deviceIds,
        newData,
      );

    }
  }, [
    ess,
    tgs,
    tes,
    instantHeatState,
    ready,
    defaultTemp,
    constantTempState,
    heatingScheduleState,
    heatingScheduleTemp,
    heatingScheduleStart,
    heatingScheduleEnd,
    isF,
    isWindFactorState,
    isFanOnlyState,
    selections,
    isFanOnly,
    heatingSchedule,
    instantHeat,
    optionalConstantTemp,
    snowSensor,
    windFactor,
    selectedMachines,
    selectedSwitches,
  ]);

  const handleDispatchAfterClickApplyButton = () => {
    dispatch(handleApplyMessageBox(false));
    setCommandConfirmed(true);
    dispatch(handleApplyCommand(true));
  };

  const handleCommandMessageBoxButtons = (e, index) => {
    switch (index) {
      case 0: {
        dispatch(handleApplyMessageBox(false));
        break;
      }
      case 1: {
        handleCreateCommand();
        handleDispatchAfterClickApplyButton();

        break;
      }
      default:
        break;
    }
  };

  const checkSchedulerStatus = heatingScheduleState;
  const start = scheduleDates.start;
  const end = scheduleDates.end;
  const startTime =
    checkSchedulerStatus &&
    `${start.time.hour}:${start.time.minute}${start.time.division}`;
  const startDate =
    checkSchedulerStatus && `${start.date.toLocaleDateString()}`;
  const endTime =
    checkSchedulerStatus &&
    `${end.time.hour}:${end.time.minute}${end.time.division}`;
  const endDate = checkSchedulerStatus && `${end.date.toLocaleDateString()}`;

  const handleMessageBox = () => {
    const messageDescription = 'confirm parameters to be applied';
    const selectedSystem = ess
      ? 'ess - electric switch system'
      : tgs
        ? 'tgs - typhoon gas system'
        : tes
          ? 'tes - typhoon electric system'
          : '';

    const selectedLocationsAndMachines = selectedSwitches;

    const messageInstantHeat = {
      title: 'instant heat program',
      state: instantHeat
        ? 'off'
        : instantHeatState.ready === false
          ? '------'
          : 'active' + ' ' + toDisplayTemp(instantHeatState.temp, isF),
    };

    const messageSnowSensor = {
      title: 'snow sensor program',
      state: snowSensor
        ? 'off'
        : ready
          ? 'ready' + ' ' + (snowThreshold ?? '---')
          : '------',
    };

    const messageFanOnly = {
      title: ' fan only program',
      state: isFanOnly ? 'off' : isFanOnlyState ? 'active' : '------',
    };

    const messageOptionalTemp = {
      title: ' optional constant temp. program',
      state: optionalConstantTemp
        ? 'off'
        : constantTempState.ready === false
          ? '------'
          : 'ready' + ' ' + toDisplayTemp(constantTempState.temp, isF),
    };

    const messageHeatingScheduleTemp = {
      title: 'heating schedule program',
      state: heatingSchedule
        ? 'off'
        : heatingScheduleState === false
          ? '------'
          : `ready ${toDisplayTemp(heatingScheduleTemp, isF)}`,
      scheduleDates:
        startDate && `${startTime} ${startDate}-${endTime} ${endDate}`,
    };

    const messageWindFactor = {
      title: 'wind factor program',
      state: windFactor ? 'off' : isWindFactorState ? 'ready' : '------',
    };
    return setMessageBoxContent({
      selectedSystem,
      selectedLocationsAndMachines,
      title: [
        messageInstantHeat,
        tgs ? messageFanOnly : messageSnowSensor,
        tgs ? messageSnowSensor : messageOptionalTemp,
        messageHeatingScheduleTemp,
        messageWindFactor,
      ],
      content: messageDescription,
    });
  };

  return (
    <Wrapper onClick={handleCreateNewCommandMessageBox}>
      <TitleContainer>
        <TitleWrapper>
          <Title>
            master control {componentTitle && `- ${componentTitle}`}
          </Title>
        </TitleWrapper>
      </TitleContainer>

      <MainSection onClick={handleMessageBoxOfSelectSystem}>
        {viewCommand && <ViewCommand />}

        {applyButton && (
          <WrapperMessageBox>
            <SelectSystemApplyMessageBox
              title={'master control command'}
              message={messageBoxContent}
              handleButtons={handleCommandMessageBoxButtons}
            />
          </WrapperMessageBox>
        )}

        <DisplayDialAndSections
          handleCreateNewCommandMessageBox={handleCreateNewCommandMessageBox}
          messageBoxOfCreateNewCommand={messageBoxOfCreateNewCommand}
        />

        {displayNoChangesMessage && (
          <WrapperMessageBox>
            <InputTempMessage
              onClose={() => setDisplayNoChangesMessage(false)}
              title={'master control command'}
              subtitle={'apply'}
              messages={[
                'no changes to apply',
                'please set or deactivate at least one program before applying',
              ]}
            />
          </WrapperMessageBox>
        )}

        <MasterControlCommandMain
          handleMessageBox={handleMessageBox}
          selectedSwitches={selectedSwitches}
          setSelectedSwitches={setSelectedSwitches}
          handleCreateNewCommandMessageBox={handleCreateNewCommandMessageBox}
          hasProgramChanges={hasProgramChanges}
          showNoChangesMessage={() => setDisplayNoChangesMessage(true)}
        />
      </MainSection>
    </Wrapper>
  );
};
export default MasterControlContents;

const Wrapper = styled.div`
  width: 983px;
  height: 613px;
  border-radius: 10px;

  ${layerA180Deg}
  ${flexDirectionColumn}  
  
  padding: 6.5rem 0;
`;
const TitleContainer = styled.div`
  width: 968px;
  height: 32px;
  border-radius: 16px;

  ${layerADark}

  ${flexBoxCenter}
  padding: 5rem 10rem;
`;
const TitleWrapper = styled.div`
  width: 100%;
  height: 90%;
  border-bottom: 2px solid #fff;
  display: flex;
  align-items: center;
`;
const Title = styled.span`
  font-size: 16px;
`;

const MainSection = styled.div`
  width: 100%;
  height: 100%;

  ${justifyContentSpaceBetween}
  padding: 8rem;
`;

const WrapperMessageBox = styled.div`
  position: absolute;

  top: 0%;
  right: 0%;
  z-index: 200;
`;
